// Round 컴포넌트 - Figma MCP 기반
import React, { PropsWithChildren } from "react";

export interface RoundProps extends Omit<PropsWithChildren, "style"> {
  size?: "lg" | "md" | "sm";
  type?: "primary" | "secondary";
  style?: "fill" | "line";
  color?: "main-gr" | "main" | "gray" | "store" | "dl" | "news";
}

function Round({
  children = "Label",
  size = "lg",
  type = "primary",
  style = "fill",
  color = "main-gr",
}: RoundProps) {
  // 스타일 맵 정의
  const styleMap = {
    lg: {
      height: "h-[28px] min-h-[28px]",
      text: "text-[14px]",
      padding: "px-[12px]",
      leading: "leading-[20px]",
      weight: "font-semibold",
      tracking: "tracking-[-0.28px]",
    },
    md: {
      height: "h-[24px] min-h-[24px]",
      text: "text-[12px]",
      padding: "px-[12px]",
      leading: "leading-[16px]",
      weight: "font-medium",
      tracking: "tracking-[-0.24px]",
    },
    sm: {
      height: "h-[20px] min-h-[20px]",
      text: "text-[10px]",
      padding: "px-[8px]",
      leading: "leading-[16px]",
      weight: "font-medium",
      tracking: "tracking-[-0.2px]",
    },
  };

  const borderColorMap = {
    gray: style === "line" ? "border-[#666666]" : "",
    main: style === "line" ? "border-[#6d2ef2]" : "",
    store:
      style === "line"
        ? size === "sm"
          ? "border-[#ffecf3]"
          : "border-[#ff4084]"
        : "",
    dl: style === "line" ? "border-[#42acbb]" : "",
    news: style === "line" ? "border-[#417af5]" : "",
    "main-gr": "",
  };

  const bgColorMap = {
    fill: {
      primary: {
        gray: "bg-[#555555]",
        main: "bg-[#6d2ef2]",
        store: "bg-[#ff4084]",
        dl: "bg-[#ecf7f8]",
        news: "bg-[#ecf2fe]",
        "main-gr":
          "bg-gradient-to-r from-[#cd3ec7] via-50% via-[#5328df] to-[#2d8efa]",
      },
      secondary: {
        gray: "bg-[#eeeeee]",
        main: "bg-[#f0eafe]",
        store: "bg-[#ffecf3]",
        dl: "bg-[#ecf7f8]",
        news: "bg-[#ecf2fe]",
        "main-gr":
          "bg-gradient-to-r from-[#cd3ec7] via-50% via-[#5328df] to-[#2d8efa]",
      },
    },
    line: {
      primary: {
        gray: "",
        main: "",
        store: "bg-[#ffecf3]",
        dl: "",
        news: "",
        "main-gr": "",
      },
      secondary: {
        gray: "",
        main: "",
        store: "bg-[#ffecf3]",
        dl: "",
        news: "",
        "main-gr": "",
      },
    },
  };

  // 배경색은 style/type/color 조합으로만 결정
  const bgClass = bgColorMap[style][type][color] || "";

  const textColorMap = {
    fill: {
      primary: {
        gray: "text-white",
        main: "text-white",
        store: "text-white",
        dl: "text-[#42acbb]",
        news: "text-[#417af5]",
        "main-gr": "text-white",
      },
      secondary: {
        gray: "text-[#666666]",
        main: "text-[#6d2ef2]",
        store: "text-[#ff4084]",
        dl: "text-[#42acbb]",
        news: "text-[#417af5]",
        "main-gr": "text-white",
      },
    },
    line: {
      primary: {
        gray: "text-[#666666]",
        main: "text-[#6d2ef2]",
        store: "text-[#ff4084]",
        dl: "text-[#42acbb]",
        news: "text-[#417af5]",
        "main-gr": "text-white",
      },
      secondary: {
        gray: "text-[#666666]",
        main: "text-[#6d2ef2]",
        store: "text-[#ff4084]",
        dl: "text-[#42acbb]",
        news: "text-[#417af5]",
        "main-gr": "text-white",
      },
    },
  };

  const borderClass =
    style === "line" ? `border border-solid ${borderColorMap[color]}` : "";
  const heightClass = styleMap[size].height;
  const paddingClass = styleMap[size].padding;
  const textClass = styleMap[size].text;
  const leadingClass = styleMap[size].leading;
  const weightClass = styleMap[size].weight;
  const trackingClass = styleMap[size].tracking;
  const textColorClass = textColorMap[style][type][color] || "text-white";

  return (
    <div
      className={`box-border content-stretch flex gap-[8px] items-center justify-center overflow-clip py-0 rounded-[20px] relative w-fit ${heightClass} ${paddingClass} ${borderClass} ${bgClass}`}
      data-name={`size=${size}, type=${type}, style=${style}, color=${color}`}
    >
      <div
        className={`flex flex-col justify-center leading-[0] not-italic relative shrink-0 ${textClass} text-center ${trackingClass} ${weightClass} whitespace-nowrap ${textColorClass}`}
      >
        <p className={leadingClass}>{children}</p>
      </div>
    </div>
  );
}

export default Round;
